import { MouseEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ApiRequestError, getApplicationDocumentDownloadUrl, post, useUpdateApplication } from '@access-cms/api-client';
import { applicationDataForDocumentHash } from '@access-cms/shared-constants';

import { Accordion, AccordionItem } from '../../../../components/Accordion';
import DetailsList, { DetailItem } from '../../../../components/DetailsList/DetailsList';
import { DigitalSignatureModal, SignatureData } from '../../../../components/DigitalSignatureModal';
import { Drawer } from '../../../../components/Drawer';
import { UploadedFile } from '../../../../components/FileUpload';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import SuccessIcon from '../../../../components/Icon/SuccessIcon';
import { normalizeTestId } from '../../../../utils';
import { formatSignatureName } from '../../../../utils/formatSignatureName';
import { PrimaryStep, SubStep } from '../../config/steps';
import { workflowConfig, WorkflowSubStepProps } from '../../config/workflow';
import { APPLICATION_TITLE } from '../../constants';
import { toPersistedApplicationData } from '../../context/application-map';
import { useApplication, WorkflowAlertVariation } from '../../context/ApplicationContext';
import { buildAttestationFromSignature } from '../../utils/attestation';
import { extractApplicationMetadata } from '../../utils/dataTransformer';
import { uploadDrawnSignatureImage } from '../../utils/signature-upload';
import FileReviewList, { FileReviewSection } from './FileReviewList';

import './FinalReviewPage.css';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface FinalReviewFormData {}

function getFieldLabels(): Record<string, Record<string, string>> {
  return {
    [PrimaryStep.PERSONAL_DETAILS]: {
      addressLine1: 'Personal Street Address Line 1',
      addressLine2: 'Personal Street Address Line 2',
      city: 'Personal Address City',
      dateOfBirth: 'Date of Birth',
      firstName: 'Legal First Name',
      lastName: 'Legal Last Name',
      middleName: 'Legal Middle Name',
      otherNames: 'Other Names Used',
      phoneNumber: 'Personal Phone Number',
      registrationEmail: 'Registration Email',
      ssn: 'Social Security Number',
      ssnConfirm: 'Social Security Number Confirmation',
      state: 'Personal Address State',
      suffix: 'Suffix',
      userCategory: 'User Category',
      workFromHome: 'Do you work from home?',
      zipCode: 'Personal Address Zip Code',
    },
    [PrimaryStep.CITIZENSHIP_DETAILS]: {
      birthCity: 'Birth City',
      birthCountry: 'Birth Country',
      birthState: 'Birth State',
      citizenshipCountry: 'Country of Citizenship',
      entryVisaType: 'On what type of visa did you enter the United States?',
      firstEntryDate: 'When did you first enter the United States?',
      immigrationDate: 'Immigration Date',
      isPermanentResident: 'Are you a Permanent Resident of the United States (U.S.)?',
      isUSCitizen: 'Are you a United States (U.S.) Citizen?',
      passportExpirationDate: 'Passport Expiration Date',
      passportIssuanceCountry: 'Passport Issuance Country',
      passportIssueDate: 'Passport Issuance Date',
      passportNumber: 'Passport Number',
      passportType: 'Passport Type',
      sex: 'Sex',
      visaNumber: 'Visa Number',
      yearsInUS: 'Years in the U.S. of the Past Five (5) Years',
    },
    [PrimaryStep.CONTRACT_DETAILS]: {
      cmsRegion: 'CMS Region/Facility',
      company: 'Company',
      contractNumber: 'Contract Number',
      laborCategory: 'Labor Category',
      requestJustification: 'Please enter a justification for needing access to CMS systems and/or infrastructure.',
      workAddressCity: 'Work Address City',
      workAddressLine1: 'Work Address Line 1',
      workAddressLine2: 'Work Address Line 2',
      workAddressState: 'Work Address State',
      workAddressZipCode: 'Work Address Zip Code',
      workEmail: 'Work Email',
      workExtension: 'Work Extension',
      workPhoneNumber: 'Work Phone Number',
    },
    [PrimaryStep.OF_306_QUESTIONS]: {
      bornMaleAfter1959: 'Were you born a male after December 31, 1959?',
      certificationConfirmed: 'Certification',
      courtMartials: 'Court-Martial',
      criminalCorrections: 'Criminal Corrections',
      currentViolations: 'Current Violations',
      federalDebts: 'Federal Debt',
      hasCourtMartial:
        'Have you been convicted by a military court-martial in the past 7 years? (If no military service, answer "No.")',
      hasCriminalCorrection:
        'During the last 7 years, have you been convicted, been imprisoned, been on probation, or been on parole?',
      hasCurrentViolations: 'Are you currently under charges for any violation of the law?',
      hasFederalDebt: 'Are you delinquent on any Federal debt?',
      hasJobDismissal:
        'During the last 5 years, have you ever been fired from a job, left a job due to specific problems, or been debarred from Federal employment for any reason?',
      hasMilitaryExperience: 'Have you ever served in the United States military?',
      hasRelatives:
        'Do any of your relatives work for the agency or government organization to which you are submitting this form?',
      hasRetirementPay:
        'Do you receive, or have you ever applied for, retirement pay, pension, or other retired pay based on military, Federal civilian, or District of Columbia Government service?',
      jobDismissals: 'Job Dismissal',
      militaryExperiences: 'Military Experience',
      registeredWithSelectiveService: 'Have you registered with the Selective Service System?',
      relatives: 'Relatives',
      retiredPays: 'Retired Pay',
      selectiveServiceExplanation: 'Explain why you have not registered with the Selective Service System.',
      violations: 'Violations',
    },
    [PrimaryStep.DOCUMENT_UPLOAD]: {
      legalDocuments: 'Legal Documents',
      otherDocuments: 'Other Documents',
      passport: 'Passport',
      pivTrainingCertificate: 'PIV Training Certificate',
      residencyDocument: 'Residency Documents',
      residencySupportingDocuments: 'Residency Documents',
      resume: 'Resume',
      usVisaDocument: 'U.S. Visa Document',
    },
  };
}

// Helper to format values for display
function formatValue(value: unknown, fieldKey?: string): string {
  if (value === null || value === undefined || value === '') {
    return 'Not provided';
  }

  // Mask Social Security Number
  if (fieldKey === 'ssn' && typeof value === 'string') {
    return '***-**-****';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  // Handle string 'yes'/'no' values (e.g., from radio buttons)
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase();
    if (lowerValue === 'yes') return 'Yes';
    if (lowerValue === 'no') return 'No';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';

    return `${value.length} item(s)`;
  }

  return String(value);
}

// Helper to get field label from mapping or fallback to formatted key
function getFieldLabel(stepId: string, key: string): string {
  const fieldLabels = getFieldLabels();

  return (
    fieldLabels[stepId]?.[key] ||
    key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
  );
}

const DOCUMENT_REVIEW_FIELDS = [
  'pivTrainingCertificate',
  'resume',
  'passport',
  'usVisaDocument',
  'residencyDocument',
  'residencySupportingDocuments',
  'legalDocuments',
  'otherDocuments',
];

const OF306_REPEATED_FIELD_ORDER: Record<string, string[]> = {
  courtMartials: ['courtMartialDate', 'courtMartialDetails'],
  criminalCorrections: ['correctionDate', 'correctionDetails'],
  dismissals: ['dismissalDate', 'dismissalDetails'],
  federalDebts: ['debtDetails'],
  militaryExperiences: ['branch', 'dateServedFrom', 'dateServedTo', 'dischargeType'],
  relatives: ['relativeDetails'],
  retiredPays: ['payType', 'otherPayType', 'stillReceivingPay', 'receiptStartDate', 'receiptEndDate'],
  violations: ['violationDate', 'violationDetails'],
};

const OF306_REPEATED_FIELD_LABELS: Record<string, Record<string, string>> = {
  courtMartials: {
    courtMartialDate: 'Date of the court-martial',
    courtMartialDetails: 'Details about the court-martial',
  },
  criminalCorrections: {
    correctionDate: 'Date of the conviction, imprisonment, probation, or parole',
    correctionDetails: 'Details about the conviction, imprisonment, probation, or parole',
  },
  dismissals: {
    dismissalDate: 'Date of the firing, quit, or debarment',
    dismissalDetails: 'Details about the firing, quit, or debarment',
  },
  federalDebts: {
    debtDetails: 'Federal Debt',
  },
  militaryExperiences: {
    branch: 'Branch of the Military',
    dateServedFrom: 'Date Served From',
    dateServedTo: 'Date Served To',
    dischargeType: 'Type of Discharge',
  },
  relatives: {
    relativeDetails: 'Relatives',
  },
  retiredPays: {
    otherPayType: 'If other, enter the type of pay you receive',
    payType: 'What type of pay do you receive?',
    receiptEndDate: 'Receipt end date',
    receiptStartDate: 'Receipt start date',
    stillReceivingPay: 'Do you still receive this pay?',
  },
  violations: {
    violationDate: 'Date of the violation of the law',
    violationDetails: 'Details about the violation of the law',
  },
};

const RETIRED_PAY_TYPE_LABELS: Record<string, string> = {
  other: 'Other retired pay based on military, Federal civilian, or District of Columbia Government service',
  pension: 'Pension',
  'retirement-pay': 'Retirement Pay',
};

const OF306_GROUP_FIELDS: Record<string, { children: string[]; primary: string }> = {
  [SubStep.COURT_MARTIAL]: {
    children: ['courtMartials'],
    primary: 'hasCourtMartial',
  },
  [SubStep.CRIMINAL_CORRECTION]: {
    children: ['criminalCorrections'],
    primary: 'hasCriminalCorrection',
  },
  [SubStep.CURRENT_VIOLATIONS]: {
    children: ['violations', 'currentViolations'],
    primary: 'hasCurrentViolations',
  },
  [SubStep.FEDERAL_DEBT]: {
    children: ['federalDebts'],
    primary: 'hasFederalDebt',
  },
  [SubStep.JOB_DISMISSAL]: {
    children: ['jobDismissals', 'dismissals'],
    primary: 'hasJobDismissal',
  },
  [SubStep.MILITARY_EXPERIENCE]: {
    children: ['militaryExperiences'],
    primary: 'hasMilitaryExperience',
  },
  [SubStep.RELATIVES]: {
    children: ['relatives'],
    primary: 'hasRelatives',
  },
  [SubStep.RETIREMENT_PAY]: {
    children: ['retiredPays'],
    primary: 'hasRetirementPay',
  },
  [SubStep.SELECTIVE_SERVICE]: {
    children: ['registeredWithSelectiveService', 'selectiveServiceExplanation'],
    primary: 'bornMaleAfter1959',
  },
};

function getRepeatedItemData(item: unknown): Record<string, unknown> | null {
  if (!item || typeof item !== 'object') return null;

  const record = item as Record<string, unknown>;
  if (record.data && typeof record.data === 'object') {
    return record.data as Record<string, unknown>;
  }

  return record;
}

function FinalReviewPage({ allFormData }: WorkflowSubStepProps<FinalReviewFormData>) {
  const {
    applicationId,
    closeSignatureModal,
    currentStepIndex,
    currentSubStepIndex,
    completeSubmission,
    goToStepForEdit,
    isReadOnly,
    isSignatureModalOpen,
    showWorkflowAlert,
  } = useApplication();
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const [expandAll, setExpandAll] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const baseTestId = 'final-review-page';

  useEffect(() => {
    if (isSignatureModalOpen) {
      setSubmissionError('');
    }
  }, [isSignatureModalOpen]);

  const handleSubmissionError = useCallback(
    (error: Error) => {
      console.error('Failed to submit application:', error);
      const message =
        error instanceof ApiRequestError
          ? error.message
          : 'There was an error submitting your application. Please try again.';

      setSubmissionError(message);
      showWorkflowAlert({
        heading: 'Submission Failed',
        text: message,
        variation: WorkflowAlertVariation.ERROR,
      });
      setIsSubmitting(false);
    },
    [showWorkflowAlert],
  );

  const updateApplication = useUpdateApplication();

  const handleDocumentReviewClick = useCallback(
    async (file: UploadedFile) => {
      const documentId = file.documentId ?? file.id;

      if (!applicationId || !documentId) {
        showWorkflowAlert({
          heading: 'Document Not Available',
          text: 'This document is not available for review. Please return to Document Upload and try again.',
          variation: WorkflowAlertVariation.ERROR,
        });

        return;
      }

      const reviewWindow = window.open('about:blank', '_blank');
      if (reviewWindow) {
        reviewWindow.opener = null;
      }

      try {
        const response = await getApplicationDocumentDownloadUrl(applicationId, documentId);
        const downloadUrl = response.data?.downloadUrl;

        if (!downloadUrl) {
          throw new Error('Document download URL was not returned.');
        }

        if (reviewWindow) {
          reviewWindow.location.href = downloadUrl;
        } else {
          window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        reviewWindow?.close();
        console.error('[FinalReview] Failed to open uploaded document', {
          applicationId,
          documentId,
          error: error instanceof Error ? error.message : String(error),
          filename: file.name,
        });
        showWorkflowAlert({
          heading: 'Document Not Available',
          text: 'The document could not be opened for review. Please make sure the malware scan is complete and try again.',
          variation: WorkflowAlertVariation.ERROR,
        });
      }
    },
    [applicationId, showWorkflowAlert],
  );

  const handleAdoptAndSign = useCallback(
    async (signatureData: SignatureData) => {
      if (!applicationId) {
        const message = 'Unable to submit application. Please refresh and try again.';
        setSubmissionError(message);
        showWorkflowAlert({
          heading: 'Application Not Found',
          text: message,
          variation: WorkflowAlertVariation.ERROR,
        });

        return;
      }

      setIsSubmitting(true);
      setSubmissionError('');

      try {
        let signatureDocumentId: string | undefined;

        if (signatureData.type === 'draw') {
          if (!signatureData.imageData) {
            throw new Error('Drawn signature image is required');
          }

          const uploadResult = await uploadDrawnSignatureImage(applicationId, signatureData.imageData);
          signatureDocumentId = uploadResult.signatureDocumentId;
        }

        const metadata = extractApplicationMetadata(allFormData);
        const saveResponse = await updateApplication.mutateAsync({
          body: {
            applicationData: toPersistedApplicationData(allFormData),
            applicationType: metadata.applicationType,
          },
          pathParams: { id: applicationId },
        });

        const savedApplicationData = saveResponse.data?.applicationData;
        if (!savedApplicationData || typeof savedApplicationData !== 'object') {
          throw new Error('Failed to sync application data before signing');
        }

        const attestation = await buildAttestationFromSignature({
          applicationTitle: APPLICATION_TITLE,
          formData: allFormData,
          persistedApplicationData: applicationDataForDocumentHash(savedApplicationData as Record<string, unknown>),
          signatureData,
          signatureDocumentId,
        });

        await updateApplication.mutateAsync({
          body: {
            applicationData: {
              attestation,
            },
          },
          pathParams: { id: applicationId },
        });

        const submitResponse = await post('/api/v1/applications/{id}/submit', {} as never, {
          pathParams: { id: applicationId },
        });

        closeSignatureModal();
        setIsSubmitting(false);
        completeSubmission(submitResponse.data?.applicationStatus ?? 'SUBMITTED');
      } catch (error) {
        if (error instanceof ApiRequestError) {
          const details = error.responseBody?.details as { eligibilityCheckData?: { notes?: string } } | undefined;

          if (details?.eligibilityCheckData?.notes) {
            error.message = `${error.message}\n\n${details.eligibilityCheckData.notes}`;
          }
        }

        handleSubmissionError(error instanceof Error ? error : new Error(String(error)));
      }
    },
    [
      allFormData,
      applicationId,
      closeSignatureModal,
      completeSubmission,
      handleSubmissionError,
      showWorkflowAlert,
      updateApplication,
    ],
  );

  // Check if user is a US citizen based on citizenship status answer
  const isUSCitizen = useMemo(() => {
    const citizenshipData = allFormData?.[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
      | { isUSCitizen?: boolean | null }
      | undefined;

    return citizenshipData?.isUSCitizen === true;
  }, [allFormData]);

  const isPermanentResident = useMemo(() => {
    const residencyData = allFormData?.[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.RESIDENCY_INFORMATION] as
      | { isPermanentResident?: boolean | null }
      | undefined;

    return !isUSCitizen && residencyData?.isPermanentResident === true;
  }, [allFormData, isUSCitizen]);

  const signerFullName = useMemo(() => {
    const personalDetails = allFormData?.[PrimaryStep.PERSONAL_DETAILS]?.[SubStep.BASIC_INFO] as
      | {
          firstName?: string;
          hasMiddleName?: boolean | null;
          lastName?: string;
          middleName?: string | null;
          suffix?: string | null;
        }
      | undefined;

    return formatSignatureName({
      firstName: personalDetails?.firstName,
      hasMiddleName: personalDetails?.hasMiddleName,
      lastName: personalDetails?.lastName,
      middleName: personalDetails?.middleName,
      suffix: personalDetails?.suffix,
    });
  }, [allFormData]);

  const createEditHandler = (stepIndex: number, substepIndex: number) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    goToStepForEdit(stepIndex, substepIndex, currentStepIndex, currentSubStepIndex);
  };

  const createEditLinks = (fieldLabel: string, stepTitle: string, stepIndex: number, substepIndex: number) =>
    isReadOnly
      ? []
      : [
          {
            ariaLabel: `Edit ${fieldLabel} in ${stepTitle}`,
            label: 'Edit',
            onClick: createEditHandler(stepIndex, substepIndex),
          },
        ];

  // Define field order for each step
  const getFieldOrder = (stepId: string): string[] => {
    const orders: Record<string, string[]> = {
      [PrimaryStep.PERSONAL_DETAILS]: [
        'firstName',
        'middleName',
        'lastName',
        'suffix',
        'otherNames',
        'dateOfBirth',
        'ssn',
        'addressLine1',
        'addressLine2',
        'city',
        'state',
        'zipCode',
        'workFromHome',
        'registrationEmail',
        'phoneNumber',
        'userCategory',
      ],
      [PrimaryStep.CITIZENSHIP_DETAILS]: [
        'isUSCitizen',
        'sex',
        'birthCountry',
        'birthCity',
        'birthState',
        'isPermanentResident',
        'citizenshipCountry',
        'firstEntryDate',
        'entryVisaType',
        'yearsInUS',
        'immigrationDate',
        'visaNumber',
        'passportIssuanceCountry',
        'passportType',
        'passportNumber',
        'passportIssueDate',
        'passportExpirationDate',
      ],
      [PrimaryStep.CONTRACT_DETAILS]: [
        'contractNumber',
        'company',
        'laborCategory',
        'cmsRegion',
        'requestJustification',
        'workEmail',
        'workPhoneNumber',
        'workExtension',
        'workAddressLine1',
        'workAddressLine2',
        'workAddressCity',
        'workAddressState',
        'workAddressZipCode',
      ],
      [PrimaryStep.DOCUMENT_UPLOAD]: [
        'pivTrainingCertificate',
        'resume',
        'passport',
        'usVisaDocument',
        'residencyDocument',
        'legalDocuments',
        'otherDocuments',
      ],
      [PrimaryStep.OF_306_QUESTIONS]: [
        'bornMaleAfter1959',
        'registeredWithSelectiveService',
        'selectiveServiceExplanation',
        'hasMilitaryExperience',
        'militaryExperiences',
        'hasCriminalCorrection',
        'criminalCorrections',
        'hasCourtMartial',
        'courtMartials',
        'hasCurrentViolations',
        'currentViolations',
        'violations',
        'hasJobDismissal',
        'jobDismissals',
        'hasFederalDebt',
        'federalDebts',
        'hasRelatives',
        'relatives',
        'hasRetirementPay',
        'retiredPays',
      ],
    };

    return orders[stepId] || [];
  };

  // Helper function to check if an OF-306 field should be shown based on conditional logic
  const shouldShowOF306Field = (key: string, substepData: Record<string, unknown>): boolean => {
    // Hide certification field from review
    if (key === 'certificationConfirmed' || key === 'certificationAccepted') {
      return false;
    }

    // Selective Service conditional fields
    if (key === 'registeredWithSelectiveService') {
      return substepData.bornMaleAfter1959 === true;
    }
    if (key === 'selectiveServiceExplanation') {
      return substepData.bornMaleAfter1959 === true && substepData.registeredWithSelectiveService === false;
    }

    // Military Experience conditional fields
    if (key === 'militaryExperiences') {
      return substepData.hasMilitaryExperience === true;
    }

    // Criminal Correction conditional fields
    if (key === 'criminalCorrections') {
      return substepData.hasCriminalCorrection === true;
    }

    // Court Martial conditional fields
    if (key === 'courtMartials') {
      return substepData.hasCourtMartial === true;
    }

    // Current Violations conditional fields - only hide the details, not the question
    if (key === 'currentViolations' || key === 'violations') {
      return substepData.hasCurrentViolations === true;
    }

    // Job Dismissal conditional fields
    if (key === 'jobDismissals' || key === 'dismissals') {
      return substepData.hasJobDismissal === true;
    }

    // Federal Debt conditional fields
    if (key === 'federalDebts') {
      return substepData.hasFederalDebt === true;
    }

    // Relatives conditional fields
    if (key === 'relatives') {
      return substepData.hasRelatives === true;
    }

    // Retirement Pay conditional fields
    if (key === 'retiredPays') {
      return substepData.hasRetirementPay === true;
    }

    // All other fields should be shown
    return true;
  };

  // Helper function to check if an immigration document field should be shown based on document type
  const shouldShowImmigrationDocumentField = (key: string, _substepData: Record<string, unknown>): boolean => {
    if (key === 'documentType' || key === 'alienRegistrationNumber' || key === 'workPermitNumber') return false;
    if (key === 'visaNumber') return true;

    return true;
  };

  const shouldShowDocumentField = (key: string): boolean => {
    if (key === 'passport') return !isUSCitizen;
    if (key === 'usVisaDocument') return !isUSCitizen && !isPermanentResident;
    if (key === 'residencyDocument') return !isUSCitizen && isPermanentResident;
    if (key === 'residencySupportingDocuments') return !isUSCitizen;

    return true;
  };

  const formatOF306RepeatedValue = (fieldKey: string, value: unknown): string => {
    if (fieldKey === 'payType' && typeof value === 'string') {
      return RETIRED_PAY_TYPE_LABELS[value] ?? formatValue(value, fieldKey);
    }

    return formatValue(value, fieldKey);
  };

  const buildOF306RepeatedItems = (key: string, value: unknown): DetailItem[] => {
    if (!Array.isArray(value) || value.length === 0) return [];

    const fieldOrder = OF306_REPEATED_FIELD_ORDER[key];
    if (!fieldOrder) return [];

    return value.flatMap((item) => {
      const itemData = getRepeatedItemData(item);
      if (!itemData) return [];

      return fieldOrder
        .filter((fieldKey) => {
          if (!(fieldKey in itemData)) return false;
          if (key === 'retiredPays' && fieldKey === 'otherPayType') return itemData.payType === 'other';
          if (key === 'retiredPays' && fieldKey === 'receiptStartDate') return itemData.stillReceivingPay === true;
          if (key === 'retiredPays' && fieldKey === 'receiptEndDate') return itemData.stillReceivingPay === false;

          return true;
        })
        .map((fieldKey) => ({
          label: OF306_REPEATED_FIELD_LABELS[key]?.[fieldKey] ?? getFieldLabel(PrimaryStep.OF_306_QUESTIONS, fieldKey),
          value: formatOF306RepeatedValue(fieldKey, itemData[fieldKey]),
        }));
    });
  };

  const renderOF306ChildItems = (items: DetailItem[]): ReactNode => {
    if (items.length === 0) return null;

    return (
      <div className='final-review-of306-group__children'>
        {items.map((item, index) => {
          const isLastItem = index === items.length - 1;

          return (
            <div
              key={`${item.label}-${index}`}
              className={`final-review-of306-group__child${
                isLastItem ? '' : ' final-review-of306-group__child--bordered'
              }`}>
              <div className='final-review-of306-group__child-label'>{item.label}</div>
              <div>{item.value}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const buildOF306GroupedDetailItems = (substepId: string, stepIndex: number, substepIndex: number): DetailItem[] => {
    const substepData = allFormData[PrimaryStep.OF_306_QUESTIONS]?.[substepId] || {};
    const groupConfig = OF306_GROUP_FIELDS[substepId];

    if (!groupConfig || !(groupConfig.primary in substepData)) return [];
    if (!shouldShowOF306Field(groupConfig.primary, substepData)) return [];

    const childItems = groupConfig.children.flatMap((key) => {
      if (!(key in substepData)) return [];
      if (!shouldShowOF306Field(key, substepData)) return [];

      const value = substepData[key];
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        return buildOF306RepeatedItems(key, value);
      }

      return [
        {
          label: getFieldLabel(PrimaryStep.OF_306_QUESTIONS, key),
          value: formatValue(value, key),
        },
      ];
    });

    const primaryLabel = getFieldLabel(PrimaryStep.OF_306_QUESTIONS, groupConfig.primary);
    const primaryValue = formatValue(substepData[groupConfig.primary], groupConfig.primary);

    return [
      {
        alignItems: childItems.length > 0 ? 'flex-start' : 'center',
        label: primaryLabel,
        links: createEditLinks(
          primaryLabel,
          workflowConfig.steps[stepIndex]?.title || 'section',
          stepIndex,
          substepIndex,
        ),
        value:
          childItems.length > 0 ? (
            <>
              <span>{primaryValue}</span>
              {renderOF306ChildItems(childItems)}
            </>
          ) : (
            primaryValue
          ),
      },
    ];
  };

  // Build detail items from form data for a substep
  const buildDetailItems = (
    stepId: string,
    substepId: string,
    stepIndex: number,
    substepIndex: number,
  ): DetailItem[] => {
    const substepData = allFormData[stepId]?.[substepId] || {};
    const items: DetailItem[] = [];
    const fieldOrder = getFieldOrder(stepId);

    // Get step title for aria-label context
    const step = workflowConfig.steps[stepIndex];
    const stepTitle = step?.title || 'section';

    // Process fields in the defined order
    const processedKeys = new Set<string>();

    fieldOrder.forEach((key) => {
      if (!(key in substepData)) return;
      processedKeys.add(key);

      const value = substepData[key];

      // Skip draft fields, internal fields, and hasMiddleName
      if (
        key.startsWith('draft') ||
        key === 'id' ||
        key === 'hasMiddleName' ||
        key === 'birthCountryCode' ||
        key === 'citizenshipCountryCode' ||
        key === 'passportIssuanceCountryCode'
      )
        return;

      if (stepId === PrimaryStep.DOCUMENT_UPLOAD && !shouldShowDocumentField(key)) return;

      // Skip OF-306 conditional fields that should not be shown
      if (stepId === PrimaryStep.OF_306_QUESTIONS && !shouldShowOF306Field(key, substepData)) return;

      // Skip immigration document fields that don't match the selected document type
      if (stepId === PrimaryStep.CITIZENSHIP_DETAILS && !shouldShowImmigrationDocumentField(key, substepData)) return;

      const fieldLabel = getFieldLabel(stepId, key);

      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        if (stepId === PrimaryStep.OF_306_QUESTIONS) {
          items.push(...buildOF306RepeatedItems(key, value));

          return;
        }

        items.push({
          label: fieldLabel,
          links: createEditLinks(fieldLabel, stepTitle, stepIndex, substepIndex),
          value: `${value.length} item(s) entered`,
        });

        return;
      }

      items.push({
        label: fieldLabel,
        links: createEditLinks(fieldLabel, stepTitle, stepIndex, substepIndex),
        value: formatValue(value, key),
      });
    });

    Object.entries(substepData).forEach(([key, value]) => {
      if (
        processedKeys.has(key) ||
        key.startsWith('draft') ||
        key === 'id' ||
        key === 'hasMiddleName' ||
        key === 'birthCountryCode' ||
        key === 'citizenshipCountryCode' ||
        key === 'passportIssuanceCountryCode' ||
        key === 'ssnConfirm'
      )
        return;

      if (stepId === PrimaryStep.DOCUMENT_UPLOAD && !shouldShowDocumentField(key)) return;

      if (stepId === PrimaryStep.OF_306_QUESTIONS && !shouldShowOF306Field(key, substepData)) return;

      if (stepId === PrimaryStep.CITIZENSHIP_DETAILS && !shouldShowImmigrationDocumentField(key, substepData)) return;

      const fieldLabel = getFieldLabel(stepId, key);

      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        if (stepId === PrimaryStep.OF_306_QUESTIONS) {
          items.push(...buildOF306RepeatedItems(key, value));

          return;
        }

        items.push({
          label: fieldLabel,
          links: createEditLinks(fieldLabel, stepTitle, stepIndex, substepIndex),
          value: `${value.length} item(s) entered`,
        });

        return;
      }

      items.push({
        label: fieldLabel,
        links: createEditLinks(fieldLabel, stepTitle, stepIndex, substepIndex),
        value: formatValue(value, key),
      });
    });

    return items;
  };

  // Build accordion items for each major step
  const accordionItems: AccordionItem[] = useMemo(() => {
    const items: AccordionItem[] = [];

    workflowConfig.steps.forEach((step, stepIndex) => {
      // Skip final-review and confirmation
      if (step.id === PrimaryStep.FINAL_REVIEW || step.id === PrimaryStep.CONFIRMATION) return;
      const stepTestId = normalizeTestId(`${baseTestId}-${step.id}`);

      // For OF-306 Questions, collect all items from all substeps into a single DetailsList
      if (step.id === PrimaryStep.OF_306_QUESTIONS) {
        const allItems: DetailItem[] = [];

        step.subSteps.forEach((substep, substepIndex) => {
          // Skip if this substep should be skipped based on form data
          if (substep.shouldSkip && substep.shouldSkip(allFormData)) return;

          const detailItems = buildOF306GroupedDetailItems(substep.id, stepIndex, substepIndex);
          allItems.push(...detailItems);
        });

        const content = (
          <div
            className='final-review-section'
            data-testid={`${stepTestId}-section`}>
            {allItems.length > 0 && <DetailsList items={allItems} />}
          </div>
        );

        items.push({
          content,
          icon: <SuccessIcon />,
          id: step.id,
          title: step.title,
        });
      } else if (step.id === PrimaryStep.DOCUMENT_UPLOAD) {
        // Special handling for Document Upload - show files with icons
        const uploadDocumentsData = allFormData[PrimaryStep.DOCUMENT_UPLOAD]?.[SubStep.UPLOAD_DOCUMENTS] || {};

        // Find the step and substep indices for document upload
        const docUploadStepIndex = workflowConfig.steps.findIndex((s) => s.id === PrimaryStep.DOCUMENT_UPLOAD);
        const docUploadSubstepIndex = 0; // upload-documents is the only substep

        const documentSections: FileReviewSection[] = [];
        const documentTypes = DOCUMENT_REVIEW_FIELDS.filter((docType) => {
          if (docType === 'passport') return !isUSCitizen;
          if (
            docType === 'usVisaDocument' ||
            docType === 'residencyDocument' ||
            docType === 'residencySupportingDocuments' ||
            docType === 'legalDocuments' ||
            docType === 'otherDocuments'
          ) {
            return false;
          }

          return true;
        });

        documentTypes.forEach((docType) => {
          const files = uploadDocumentsData[docType] as UploadedFile[] | undefined;
          if (files && Array.isArray(files)) {
            const reviewFiles = files.filter(
              (f) => !f.error && !f.isUploading && (f.uploadStatus === 'associated' || f.documentId),
            );

            if (reviewFiles.length > 0) {
              documentSections.push({
                files: reviewFiles,
                title: getFieldLabel(PrimaryStep.DOCUMENT_UPLOAD, docType),
              });
            }
          }
        });

        const toReviewFiles = (field: string): UploadedFile[] => {
          const files = uploadDocumentsData[field] as UploadedFile[] | undefined;

          return (files ?? []).filter(
            (file) => !file.error && !file.isUploading && (file.uploadStatus === 'associated' || file.documentId),
          );
        };

        if (!isUSCitizen) {
          const primaryResidencyFiles = toReviewFiles(isPermanentResident ? 'residencyDocument' : 'usVisaDocument');
          const residencySupportingFiles = toReviewFiles('residencySupportingDocuments');
          if (primaryResidencyFiles.length > 0 || residencySupportingFiles.length > 0) {
            documentSections.push({
              groups: [
                ...(primaryResidencyFiles.length > 0
                  ? [{ files: primaryResidencyFiles, title: 'U.S. visa or U.S. Permanent Resident card' }]
                  : []),
                ...(residencySupportingFiles.length > 0
                  ? [{ files: residencySupportingFiles, title: 'Other U.S. residency documents' }]
                  : []),
              ],
              title: 'Residency Documents',
            });
          }
        }

        const legalFiles = toReviewFiles('legalDocuments');
        const otherFiles = toReviewFiles('otherDocuments');
        if (legalFiles.length > 0 || otherFiles.length > 0) {
          documentSections.push({
            files: [...legalFiles, ...otherFiles],
            title: 'Legal and Other Documents',
          });
        }

        const content = (
          <div
            className='final-review-section'
            data-testid={`${stepTestId}-section`}>
            <div data-testid={`${stepTestId}-files`}>
              <FileReviewList
                sections={documentSections}
                onEditClick={isReadOnly ? undefined : createEditHandler(docUploadStepIndex, docUploadSubstepIndex)}
                onFileClick={handleDocumentReviewClick}
              />
            </div>
          </div>
        );

        items.push({
          content,
          icon: <SuccessIcon />,
          id: step.id,
          title: step.title,
        });
      } else {
        // Collect all detail items for substeps to determine which is last
        const substepDetailLists = step.subSteps
          .map((substep, substepIndex) => {
            // Skip if this substep should be skipped based on form data
            if (substep.shouldSkip && substep.shouldSkip(allFormData)) return null;

            const detailItems = buildDetailItems(step.id, substep.id, stepIndex, substepIndex);

            if (detailItems.length === 0) return null;

            return { detailItems, substepId: substep.id };
          })
          .filter(Boolean) as { substepId: string; detailItems: DetailItem[] }[];

        const content = (
          <div
            className='final-review-section'
            data-testid={`${stepTestId}-section`}>
            {substepDetailLists.map((item, index) => {
              const isLastDetailsList = index === substepDetailLists.length - 1;

              return (
                <DetailsList
                  key={item.substepId}
                  items={item.detailItems}
                  showBorderOnLastItem={!isLastDetailsList}
                />
              );
            })}
          </div>
        );

        items.push({
          content,
          icon: <SuccessIcon />,
          id: step.id,
          title: step.title,
        });
      }
    });

    return items;
  }, [allFormData, handleDocumentReviewClick, isPermanentResident, isUSCitizen]);

  const handleExpandAll = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setExpandAll(!expandAll);
  };

  const handlePrint = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    // Expand all accordions before printing
    setExpandAll(true);

    // Wait for accordions to expand before triggering print
    setTimeout(() => {
      window.print();
    }, 100);
  };

  return (
    <div
      className='final-review-page'
      data-testid={baseTestId}>
      <p
        className='ds-u-margin-bottom--3 ds-u-color--muted'
        data-testid={`${baseTestId}-intro`}>
        {isReadOnly
          ? 'Your application has been submitted. You may review your information below.'
          : 'Review your application information below. Click "Edit" to make changes to any section.'}
      </p>

      <div
        className='final-review-page__actions ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-actions`}>
        <a
          aria-label='Print this page for your records'
          className='ds-c-link'
          data-testid={`${baseTestId}-print`}
          href='#'
          onClick={handlePrint}>
          Print This Page
        </a>
        <a
          aria-label={expandAll ? 'Collapse all sections' : 'Expand all sections'}
          className='ds-c-link'
          data-testid={`${baseTestId}-expand-toggle`}
          href='#'
          onClick={handleExpandAll}>
          {expandAll ? 'Collapse All' : 'Expand All'}
        </a>
      </div>

      <Accordion
        key={expandAll ? 'expanded' : 'collapsed'}
        allowMultipleOpen
        data-testid={`${baseTestId}-accordion`}
        defaultAllOpen={expandAll}
        items={accordionItems}
      />

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      {!isReadOnly && (
        <DigitalSignatureModal
          applicationId={applicationId ?? undefined}
          applicationTitle={APPLICATION_TITLE}
          data-testid={`${baseTestId}-signature-modal`}
          fullName={signerFullName}
          isOpen={isSignatureModalOpen}
          isSubmitting={isSubmitting}
          submitError={submissionError}
          onAdoptAndSign={handleAdoptAndSign}
          onClose={closeSignatureModal}
        />
      )}

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Final Review'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Reviewing Your Application</h3>
          <p>
            Use this page to review all the data and details you have entered. Open each drawer to see your answers.
          </p>

          <h4 className='ds-u-margin-top--3'>Making Changes</h4>
          <p>
            Click the &quot;Edit&quot; link next to any field to go back and modify that information. After making
            changes, click &quot;Save&quot; to return to this review page.
          </p>

          <h4 className='ds-u-margin-top--3'>Printing Your Application</h4>
          <p>
            Use the &quot;Print This Page&quot; link at the top to print a copy of your application for your records.
          </p>

          <h4 className='ds-u-margin-top--3'>Submitting Your Application</h4>
          <p>
            When you are done reviewing, you may submit your application. To submit, click the &quot;Submit
            Application&quot; button. Then, sign the application in the pop-up that appears.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default FinalReviewPage;
